'use client'
import Image from 'next/image'
import React, { memo, useMemo, useState } from 'react'
import { Skeleton } from '@mantine/core'
// ----------------------------------------------------------------------------

const AmenitiesSection = ({ data, loading }) => {
  const [showAllAmenities, setShowAllAmenities] = useState(false)

  // Build amenities list safely
  const amenities =
    data?.data?.property_amenities?.map((item) => ({
      amenityName: item?.amenity_display_name || 'Unknown Amenity',
      amenity_logo_url:
        item?.amenity_logo_url ||
        'https://QuickFeatures.s3.eu-central-1.amazonaws.com/QuickFeatures/skillet.svg',
    })) || []
  // Memoized subset or full list depending on toggle
  const displayedAmenities = useMemo(() => {
    return showAllAmenities ? amenities : amenities.slice(0, 15)
  }, [amenities, showAllAmenities])

  // Skeleton rows component
  const SkeletonRows = useMemo(
    () =>
      Array.from({ length: 5 }, (_, rowIndex) => (
        <div
          key={`skeleton-row-${rowIndex}`}
          className='flex flex-row gap-[15px]'
        >
          {Array.from({ length: 3 }, (_, colIndex) => (
            <div
              key={`skeleton-col-${colIndex}`}
              className='w-[237px] h-[30px] flex items-center opacity-100 bg-white gap-2'
            >
              <Skeleton
                visible={loading}
                width={30}
                height={30}
                className='w-[30px] h-[30px] rounded-md flex-shrink-0'
              />
              <Skeleton
                visible={loading}
                width={
                  colIndex === 0
                    ? 120
                    : colIndex === 1
                      ? 150
                      : 100 + rowIndex * 20
                }
                height={15}
                className='rounded-sm'
              />
            </div>
          ))}
        </div>
      )),
    [],
  )

  // Early return if no amenities
  if (!loading && (!amenities || amenities?.length === 0)) {
    return null
  }

  return (
    <section id='offers-section' className='w-[800px] opacity-100'>
      {/* Header */}
      {loading ? (
        <Skeleton width={200} height={27} className='mb-0.5 mt-2' />
      ) : (
        <h2 className='font-semibold text-[18px] leading-[150%] text-[#1E1F24] mb-0.5 mt-2'>
          What This Place Offers
        </h2>
      )}

      <div className='w-full mt-5 flex flex-col gap-5 p-0'>
        {/* Amenities Grid */}
        {loading || !displayedAmenities.length ? (
          SkeletonRows
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6'>
            {displayedAmenities.map((amenity, index) => (
              <div
                key={index}
                className='flex items-center gap-3 text-[#191919]'
              >
                <Image
                  src={amenity?.amenity_logo_url}
                  alt={amenity?.amenityName || 'Amenity icon'}
                  width={30}
                  height={30}
                  className='w-[30px] h-[30px] object-contain flex-shrink-0'
                />
                <span className='font-medium text-[13px] leading-[160%]'>
                  {amenity?.amenityName}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Show All / Show Less Button */}
        {!loading && amenities?.length > 15 && (
          <div className='flex justify-start mt-4 mx-[-8px]'>
            <button
              className='w-[168px] h-[37px] rounded-[8px] border border-[#D2D2D2] bg-white text-[#191919] justify-center items-center gap-1 opacity-100 font-poppins 
              font-medium text-[13px] leading-[160%] hover:bg-gray-50 hover:border-gray-300 transition-colors duration-150 cursor-pointer'
              onClick={() => setShowAllAmenities(!showAllAmenities)}
              aria-label={
                showAllAmenities ? 'Show fewer amenities' : 'Show all amenities'
              }
            >
              <span className='w-[124px] h-[21px] font-medium text-[13px] leading-[160%] text-[#191919] text-center'>
                {showAllAmenities
                  ? 'Show less amenities'
                  : 'Show all amenities'}
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export default memo(AmenitiesSection)
